import { useMemo, useCallback } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import _, { uniqBy } from "lodash"

import {
  DEFAULT_EVENT_FILTER_VALUE,
  DEFAULT_FILTER_VALUE,
  Q_EVENT_GROUP,
  Q_VENUE,
} from "constant"
import dayjs from "config/dayjs"
import { filterCurrentFeature, filterUpcomingFeature } from "utils/feature"

import { useGeoLocation } from "providers/geolocation"
import { useVenue } from "providers/venue"
import { useGetEvents } from "providers/venue/modules/data"
import { EventData } from "providers/venue/types"
import { useVenueController } from "hooks/venue/useVenueController"
import { useRecommendedEvent } from "hooks/sponsored-content/recommended"

const sortEventsByDate = (events: EventData[]) => {
  return events.sort(
    (firstEvent, secondEvent) =>
      dayjs(firstEvent.properties.start_date).valueOf() -
      dayjs(secondEvent.properties.start_date).valueOf()
  )
}

export const useEvent = () => {
  const { data: events = [] } = useGetEvents()
  const { venues, isMultiVenueProject } = useVenue()
  const navigate = useNavigate()
  const { locationVenue } = useGeoLocation()
  const locationVenueId = _.get(locationVenue, "id")
  const { viewingVenue: venueFilter } = useVenueController(locationVenueId)
  const [searchParams] = useSearchParams()
  const { recommendedEvents } = useRecommendedEvent()

  // Query String filters
  const activeGroupEvent = useMemo(
    () => searchParams.get(Q_EVENT_GROUP) || DEFAULT_EVENT_FILTER_VALUE,
    [searchParams]
  )

  const changeVenueFilter = useCallback(
    (venueId) => {
      if (venueId) searchParams.set(Q_VENUE, venueId)
      navigate(`/events/?${searchParams.toString()}`, { replace: true })
    },
    [navigate, searchParams]
  )

  const currentEvents = useMemo(
    () => events.filter(filterCurrentFeature),
    [events]
  )

  const currentRecommendedEvents = useMemo(
    () => recommendedEvents.data.filter(filterCurrentFeature),
    [recommendedEvents]
  )

  const viewingVenueCurrentEvent = useMemo(
    () =>
      currentEvents?.filter(
        ({ properties }) =>
          venueFilter === DEFAULT_FILTER_VALUE ||
          properties.venue_id === venueFilter ||
          !properties.venue_id
      ),
    [currentEvents, venueFilter]
  )

  const viewingVenueCurrentRecommendedEvent = useMemo(
    () =>
      currentRecommendedEvents?.filter(
        ({ properties }) =>
          venueFilter === DEFAULT_FILTER_VALUE ||
          properties.venue_id === venueFilter ||
          !properties.venue_id
      ),
    [currentRecommendedEvents, venueFilter]
  )

  const upcomingEvents = useMemo(
    () => events.filter(filterUpcomingFeature),
    [events]
  )

  const upcomingRecommendedEvents = useMemo(
    () => recommendedEvents.data.filter(filterUpcomingFeature),
    [recommendedEvents]
  )

  const viewingVenueUpcomingEvents = useMemo(
    () =>
      upcomingEvents?.filter(
        ({ properties }) =>
          venueFilter === DEFAULT_FILTER_VALUE ||
          properties.venue_id === venueFilter ||
          !properties.venue_id
      ),
    [upcomingEvents, venueFilter]
  )

  const viewingVenueUpcomingRecommendedEvents = useMemo(
    () =>
      upcomingRecommendedEvents?.filter(
        ({ properties }) =>
          venueFilter === DEFAULT_FILTER_VALUE ||
          properties.venue_id === venueFilter ||
          !properties.venue_id
      ),
    [upcomingRecommendedEvents, venueFilter]
  )

  const groupEvents = useMemo(
    () => ({
      current: uniqBy(
        [
          ...viewingVenueCurrentRecommendedEvent,
          ...sortEventsByDate(viewingVenueCurrentEvent),
        ],
        "id"
      ),
      upcoming: uniqBy(
        [
          ...viewingVenueUpcomingRecommendedEvents,
          ...sortEventsByDate(viewingVenueUpcomingEvents),
        ],
        "id"
      ),
    }),
    [
      viewingVenueCurrentRecommendedEvent,
      viewingVenueUpcomingRecommendedEvents,
      viewingVenueCurrentEvent,
      viewingVenueUpcomingEvents,
    ]
  )

  const handleChange = useCallback(
    (e, eventGroup) => {
      if (eventGroup === null) return
      searchParams.set(Q_EVENT_GROUP, eventGroup)
      navigate(`/events?${searchParams.toString()}`, { replace: true })
    },
    [navigate, searchParams]
  )

  // Add All Events for new layout (current + upcoming) and can support old design (separated current/upcoming)
  const sanitizedEvents = useMemo(() => {
    return _.uniqBy(
      [
        ...viewingVenueCurrentRecommendedEvent,
        ...viewingVenueUpcomingRecommendedEvents,
        ...sortEventsByDate(viewingVenueCurrentEvent),
        ...sortEventsByDate(viewingVenueUpcomingEvents),
      ],
      "id"
    )
  }, [
    viewingVenueCurrentRecommendedEvent,
    viewingVenueCurrentEvent,
    viewingVenueUpcomingRecommendedEvents,
    viewingVenueUpcomingEvents,
  ])

  return {
    venues,
    venueFilter,
    locationVenueId,
    isMultiVenueProject,
    groupEvents,
    activeGroupEvent,
    events: sanitizedEvents,
    handleChange,
    changeVenueFilter,
  }
}
